import Cookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import {
	createWebSocketMessage,
	parseWebSocketMessage,
	WebSocketEventType,
	WebSocketMessage,
} from './events';

// Base WebSocket URL - will be enhanced with token
const BASE_WS_URL = process.env.REACT_APP_WS_URL || 'wss://your-websocket-server.com';

/**
 * Hook to connect to WebSocket and handle events
 * @param {Object} options Additional options for the WebSocket connection
 * @param {Function} onMessage Callback function to handle incoming messages
 * @param {Function} onOpen Callback when connection is established
 * @param {Function} onClose Callback when connection is closed
 * @param {Function} onError Callback when an error occurs
 */
export const useWebSocketConnection = ({
	onMessage,
	onOpen,
	onClose,
	onError,
	additionalOptions = {},
}: {
	onMessage?: (data: WebSocketMessage) => void;
	onOpen?: () => void;
	onClose?: (event: CloseEvent) => void;
	onError?: (event: Event) => void;
	additionalOptions?: Record<string, any>;
}) => {
	// Get token directly from cookie
	const getTokenFromCookie = () => Cookies.get('accessToken') || null;

	// Get token from cookie
	const tokenFromCookie = useMemo(() => {
		return getTokenFromCookie();
	}, []);

	// Construct full WebSocket URL with token
	const WS_URL = useMemo(() => {
		if (!tokenFromCookie) {
			console.warn('No auth token found for WebSocket connection');
			return BASE_WS_URL;
		}

		// Add token as query parameter
		const separator = BASE_WS_URL.includes('?') ? '&' : '?';
		return `${BASE_WS_URL}${separator}token=${tokenFromCookie}`;
	}, [tokenFromCookie]);

	const { sendMessage, lastMessage, readyState, getWebSocket } = useWebSocket(WS_URL, {
		onOpen: () => {
			console.log('WebSocket connection established');
			onOpen?.();
		},
		onClose: (event) => {
			console.log('WebSocket connection closed', event);
			onClose?.(event);
		},
		onError: (event) => {
			console.error('WebSocket error:', event);
			onError?.(event);
		},
		shouldReconnect: (closeEvent) => true, // Auto reconnect on connection loss
		reconnectAttempts: 1000,
		reconnectInterval: 3000,
		...additionalOptions,
	});

	// Handle incoming messages
	useEffect(() => {
		if (lastMessage && onMessage) {
			const parsedMessage = parseWebSocketMessage(lastMessage);
			onMessage(parsedMessage);
		}
	}, [lastMessage, onMessage]);

	// Helper function to send JSON data
	const sendJsonMessage = useCallback(
		(data: any) => {
			sendMessage(JSON.stringify(data));
		},
		[sendMessage]
	);

	// Send a typed message using our message format
	const sendEvent = useCallback(
		(type: WebSocketEventType | string, payload: any) => {
			const message = createWebSocketMessage(type, payload);
			sendJsonMessage(message);
		},
		[sendJsonMessage]
	);

	// Send pong message every 30 seconds to keep connection alive
	const pongIntervalRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		// Only setup the interval when connection is open
		if (readyState === ReadyState.OPEN) {
			// Clear any existing interval first
			if (pongIntervalRef.current) {
				clearInterval(pongIntervalRef.current);
			}

			// Set up new interval to send pong every 30 seconds
			pongIntervalRef.current = setInterval(() => {
				sendEvent('PONG', { timestamp: Date.now() });
				console.log('Sent PONG event to keep connection alive');
			}, 30000);
		}

		// Clean up interval on unmount or when connection closes
		return () => {
			if (pongIntervalRef.current) {
				clearInterval(pongIntervalRef.current);
				pongIntervalRef.current = null;
			}
		};
	}, [readyState, sendEvent]);

	// Connection status as a string
	const connectionStatus = {
		[ReadyState.CONNECTING]: 'Connecting',
		[ReadyState.OPEN]: 'Open',
		[ReadyState.CLOSING]: 'Closing',
		[ReadyState.CLOSED]: 'Closed',
		[ReadyState.UNINSTANTIATED]: 'Uninstantiated',
	}[readyState];

	return {
		sendMessage,
		sendJsonMessage,
		sendEvent,
		lastMessage,
		readyState,
		connectionStatus,
		isConnecting: readyState === ReadyState.CONNECTING,
		isOpen: readyState === ReadyState.OPEN,
		isClosed: readyState === ReadyState.CLOSED,
		getWebSocket,
	};
};

export default useWebSocketConnection;
